import axios from "axios";
import { getAuthApiRoute } from "../utils/api.utils";
import {
  getAccessToken,
  getRefreshToken,
  notifySessionExpired,
  setAuthTokens,
} from "./authStorage";

const httpService = axios.create({});
var refreshRequest = null;

function getAuthorizationToken(headers) {
  if (!headers) return null;
  var value =
    typeof headers.get === "function"
      ? headers.get("Authorization")
      : headers.Authorization || headers.authorization;
  if (!value || typeof value !== "string") return null;
  return value.replace(/^Bearer\s+/i, "");
}

function setAuthorizationHeader(request, token) {
  if (!request.headers) request.headers = {};
  if (typeof request.headers.set === "function") {
    request.headers.set("Authorization", "Bearer " + token);
  } else {
    request.headers.Authorization = "Bearer " + token;
  }
}

function isTerminalRefreshError(error) {
  var status = error && error.response && error.response.status;
  return [400, 401, 403, 404, 422].indexOf(status) !== -1;
}

function requestFreshTokens() {
  if (refreshRequest) return refreshRequest;

  var refreshToken = getRefreshToken();
  if (!refreshToken) {
    notifySessionExpired("missing-refresh-token");
    return Promise.reject(new Error("No refresh token is available"));
  }

  refreshRequest = axios
    .post(
      getAuthApiRoute("refresh-token"),
      { refreshToken: refreshToken },
      {
        headers: {
          "Content-Type": "application/json",
          "X-App-Identifier": "MNB-WebTV",
        },
        timeout: 15000,
      }
    )
    .then(function (response) {
      var body = response.data;
      var data =
        body && Object.prototype.hasOwnProperty.call(body, "data")
          ? body.data
          : body;

      if (data && data.tokens) {
        data = Object.assign({}, data, data.tokens);
      }

      if (!data || !data.accessToken) {
        var invalidResponseError = new Error(
          "The refresh response did not include an access token"
        );
        invalidResponseError.isInvalidRefreshResponse = true;
        throw invalidResponseError;
      }

      // New servers rotate both credentials. Keeping the submitted refresh
      // token is the migration fallback for older compatible deployments.
      var tokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || refreshToken,
      };
      setAuthTokens(tokens);
      return tokens;
    })
    .catch(function (error) {
      // Network errors and 5xx responses are transient. Keep the current
      // session so a later request can retry instead of logging the user out.
      if (error.isInvalidRefreshResponse || isTerminalRefreshError(error)) {
        notifySessionExpired("refresh-rejected");
      }
      throw error;
    })
    .then(
      function (value) {
        refreshRequest = null;
        return value;
      },
      function (error) {
        refreshRequest = null;
        throw error;
      }
    );

  return refreshRequest;
}

export function refreshAuthSession() {
  return requestFreshTokens();
}

httpService.interceptors.request.use(
  (request) => {
    try {
      const accessToken = getAccessToken();
      if (!request.headers) request.headers = {};

      if (
        accessToken &&
        !request.skipAuth &&
        !getAuthorizationToken(request.headers)
      ) {
        setAuthorizationHeader(request, accessToken);
      }

      if (typeof request.headers.set === "function") {
        request.headers.set("X-App-Identifier", "MNB-WebTV");
      } else {
        request.headers["X-App-Identifier"] = "MNB-WebTV";
      }

      return request;
    } catch (error) {
      console.log("ERROR on Intercepting Access Token" + error);
      return request;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

httpService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    var status = error && error.response && error.response.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.skipAuthRefresh
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    var requestToken = getAuthorizationToken(originalRequest.headers);
    var currentToken = getAccessToken();

    // Anonymous 401 responses are ordinary authorization failures, not an
    // expired signed-in session.
    if (!requestToken && !currentToken) {
      return Promise.reject(error);
    }

    // A concurrent request may receive its 401 after another request already
    // completed token rotation. Retry it with the current token directly.
    if (requestToken && currentToken && requestToken !== currentToken) {
      setAuthorizationHeader(originalRequest, currentToken);
      return httpService(originalRequest);
    }

    if (!getRefreshToken()) {
      notifySessionExpired("missing-refresh-token");
      return Promise.reject(error);
    }

    try {
      var tokens = await requestFreshTokens();
      setAuthorizationHeader(originalRequest, tokens.accessToken);
      return httpService(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

export default httpService;
